import React, { useCallback, useEffect, useState } from "react";
import {
  Redirect,
  useHistory,
} from "react-router-dom";
import MyLoader from "../Spinner/MyLoader";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import myToast from "../Utils/MyToast";
import axios from "axios";
import SessionManager from "../Utils/SessionManager";
import BackButton from "../ChildComponent/BackButton";
import MdlAddSession from "../dialog/MdlAddSession";
import MdlChangeSessionCycle from "../dialog/MdlChangeSessionCycle";
import MdlChangeSessionName from "../dialog/MdlChangeSessionName";

const ACCENT = COLORS.primary_color_shade6 || "#039BE5";

function formatCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("en-IN");
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getStatusClass(status) {
  return String(status || "")
    .toLowerCase()
    .replace(/\s+/g, "-");
}

function canRemoveSession(session) {
  return (
    Number(session?.student_count || 0) === 0 &&
    Number(session?.class_fee_added || 0) === 0 &&
    Number(session?.class_subject_added || 0) === 0
  );
}

function canChangeSessionCycle(session) {
  return Number(session?.class_fee_added || 0) === 0;
}

const SessionSettingsList = () => {
  const history = useHistory();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [showAddSession, setShowAddSession] = useState(false);
  const [cycleSession, setCycleSession] = useState(null);
  const [nameSession, setNameSession] = useState(null);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const schoolCode = SessionManager.getSchoolCode();
      const url =
        process.env.REACT_APP_SERVER_NAME +
        process.env.REACT_APP_TARGET_DIR +
        "getSessionSettings.php";

      const response = await axios.get(url, {
        params: { school_code: schoolCode },
      });

      if (response.data.api_status === "OK") {
        setSessions(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        myToast.notify_warning(
          response.data.api_remark || "Could not load session settings."
        );
      }
    } catch (error) {
      console.error("SessionSettings fetch error:", error);
      myToast.notify_error("An error occurred while fetching session settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleRemoveSession = useCallback(
    async (session) => {
      const sessionName = session.session_name || "this session";
      if (
        !window.confirm(
          `Remove "${sessionName}"?\n\nThis will permanently delete the session along with its classes, boards, mediums, and subjects. This session has no students, no class fee, and no class subject added. This action cannot be undone.`
        )
      ) {
        return;
      }

      setRemovingId(session.session_id);
      try {
        const schoolCode = SessionManager.getSchoolCode();
        const staffId = SessionManager.getStaffDetails()?.staff_id;
        if (!staffId) {
          myToast.notify_warning("Staff session not found. Please log in again.");
          return;
        }

        const url =
          process.env.REACT_APP_SERVER_NAME +
          process.env.REACT_APP_TARGET_DIR +
          "removeSession.php";

        const response = await axios.get(url, {
          params: {
            school_code: schoolCode,
            session_id: session.session_id,
            staff_id: staffId,
          },
        });

        if (response.data.api_status === "OK") {
          myToast.notify_success(
            response.data.api_remark || "Session removed successfully."
          );
          fetchSessions();
        } else {
          myToast.notify_warning(
            response.data.api_remark || "Could not remove session."
          );
        }
      } catch (error) {
        console.error("Remove session error:", error);
        myToast.notify_error("An error occurred while removing the session.");
      } finally {
        setRemovingId(null);
      }
    },
    [fetchSessions]
  );

  const handleToggleAddSession = useCallback(() => {
    setShowAddSession((prev) => !prev);
  }, []);

  return (
    <div className="content" style={{ background: COLORS.white }}>
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <a href="#">Session Settings</a>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton onClick={() => history.goBack()} />
        </div>
      </div>

      <div
        className="bg_page_white_rounded box_shadow ss-page"
        style={{ "--ss-accent": ACCENT, "--mMargin": "50px" }}
      >
        <div className="ss-hero">
          <div className="ss-hero-main">
            <span className="ss-hero-icon" aria-hidden="true">
              <i className="fa fa-calendar" />
            </span>
            <div>
              <h2 className="ss-heading">Academic Sessions</h2>
              <p className="ss-subheading">Manage session setup and overview</p>
            </div>
          </div>

          <div className="ss-hero-total">
            <div className="ss-hero-total-label">Total Sessions</div>
            <div className="ss-hero-total-value">
              {loading ? "…" : sessions.length}
            </div>
          </div>

          <button
            type="button"
            className="ss-add-btn"
            onClick={handleToggleAddSession}
          >
            <span className="ss-add-btn-icon" aria-hidden="true">
              <i className="fa fa-plus" />
            </span>
            Add New Session
          </button>
        </div>

        {showAddSession && (
          <MdlAddSession
            actionClose={handleToggleAddSession}
            action2={fetchSessions}
            sessions={sessions}
          />
        )}

        {cycleSession && (
          <MdlChangeSessionCycle
            session={cycleSession}
            actionClose={() => setCycleSession(null)}
            action2={fetchSessions}
          />
        )}

        {nameSession && (
          <MdlChangeSessionName
            session={nameSession}
            actionClose={() => setNameSession(null)}
            action2={fetchSessions}
          />
        )}

        {loading ? (
          <div className="ss-loader-wrap">
            <MyLoader />
          </div>
        ) : sessions.length === 0 ? (
          <div className="ss-empty">
            <div className="ss-empty-icon">
              <i className="fa fa-calendar-o" aria-hidden="true" />
            </div>
            <div className="ss-empty-title">No sessions found</div>
            <div className="ss-empty-text">
              Add a new academic session to get started.
            </div>
          </div>
        ) : (
            <div className="ss-list">
              {sessions.map((session) => {
                const classCount = Number(session.class_count || 0);
                const feeAdded = Number(session.class_fee_added || 0);
                const feeComplete = session.is_all_class_fee_added === "Yes";
                const subjectAdded = Number(session.class_subject_added || 0);
                const subjectTotal = Number(session.class_subject_count || 0);
                const subjectComplete = session.is_all_class_subject_added === "Yes";
                const showRemove = canRemoveSession(session);
                const showChangeCycle = canChangeSessionCycle(session);
                const isRemoving = removingId === session.session_id;

                return (
                  <div
                    className={`ss-card ss-card--${getStatusClass(session.status)}`}
                    key={session.session_id}
                  >
                    <div className="ss-card-head">
                      <div className="ss-card-title-wrap">
                        <div className="ss-session-icon" aria-hidden="true">
                          <i className="fa fa-book" />
                        </div>
                        <div className="ss-card-title-block">
                          <div className="ss-session-name">
                            {session.session_name || "—"}
                          </div>
                          <div className="ss-date-row">
                            <i className="fa fa-calendar-o" aria-hidden="true" />
                            <span className="ss-date-range">
                              {formatDate(session.start_date)} —{" "}
                              {formatDate(session.end_date)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="ss-card-aside">
                        <div className="ss-action-group">
                          {showChangeCycle && (
                            <button
                              type="button"
                              className="ss-action-btn ss-action-btn--cycle"
                              onClick={() => setCycleSession(session)}
                            >
                              <span className="ss-action-btn-icon" aria-hidden="true">
                                <i className="fa fa-refresh" />
                              </span>
                              <span className="ss-action-btn-text">Change Cycle</span>
                            </button>
                          )}
                          <button
                            type="button"
                            className="ss-action-btn ss-action-btn--name"
                            onClick={() => setNameSession(session)}
                          >
                            <span className="ss-action-btn-icon" aria-hidden="true">
                              <i className="fa fa-pencil" />
                            </span>
                            <span className="ss-action-btn-text">Change Name</span>
                          </button>
                          {showRemove && (
                            <button
                              type="button"
                              className="ss-action-btn ss-action-btn--remove"
                              disabled={isRemoving}
                              onClick={() => handleRemoveSession(session)}
                            >
                              <span className="ss-action-btn-icon" aria-hidden="true">
                                <i className="fa fa-trash" />
                              </span>
                              <span className="ss-action-btn-text">
                                {isRemoving ? "Removing…" : "Remove"}
                              </span>
                            </button>
                          )}
                          <span
                            className={`ss-status ss-status--${getStatusClass(
                              session.status
                            )}`}
                          >
                            {session.status || "—"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="ss-metrics">
                      <div className="ss-metric-chip ss-metric-chip--students">
                        <span className="ss-metric-icon">
                          <i className="fa fa-users" aria-hidden="true" />
                        </span>
                        <div>
                          <div className="ss-metric-label">Students</div>
                          <div className="ss-metric-value">
                            {formatCount(session.student_count)}
                          </div>
                        </div>
                      </div>

                      <div className="ss-metric-chip ss-metric-chip--classes">
                        <span className="ss-metric-icon">
                          <i className="fa fa-graduation-cap" aria-hidden="true" />
                        </span>
                        <div>
                          <div className="ss-metric-label">Classes</div>
                          <div className="ss-metric-value">
                            {formatCount(classCount)}
                          </div>
                        </div>
                      </div>

                      <div
                        className={`ss-metric-chip ss-metric-chip--fee ${
                          feeComplete ? "ss-metric-chip--fee-done" : ""
                        }`}
                      >
                        <span className="ss-metric-icon">
                          <i
                            className={`fa ${
                              feeComplete ? "fa-check-circle" : "fa-inr"
                            }`}
                            aria-hidden="true"
                          />
                        </span>
                        <div>
                          <div className="ss-metric-label">Class Fee Added</div>
                          <div className="ss-metric-value">
                            {formatCount(feeAdded)}
                            {classCount > 0 ? ` / ${formatCount(classCount)}` : ""}
                          </div>
                        </div>
                      </div>

                      <div
                        className={`ss-metric-chip ss-metric-chip--subject ${
                          subjectComplete ? "ss-metric-chip--subject-done" : ""
                        }`}
                      >
                        <span className="ss-metric-icon">
                          <i
                            className={`fa ${
                              subjectComplete ? "fa-check-circle" : "fa-book"
                            }`}
                            aria-hidden="true"
                          />
                        </span>
                        <div>
                          <div className="ss-metric-label">Class Subject</div>
                          <div className="ss-metric-value">
                            {formatCount(subjectAdded)}
                            {classCount > 0 ? ` / ${formatCount(classCount)}` : ""}
                          </div>
                          {subjectTotal > 0 && (
                            <div className="ss-metric-subvalue">
                              {formatCount(subjectTotal)} subjects
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="ss-metric-chip ss-metric-chip--board">
                        <span className="ss-metric-icon">
                          <i className="fa fa-university" aria-hidden="true" />
                        </span>
                        <div>
                          <div className="ss-metric-label">Board</div>
                          <div
                            className="ss-metric-value ss-metric-value--text"
                            title={session.board || "—"}
                          >
                            {session.board || "—"}
                          </div>
                        </div>
                      </div>

                      <div className="ss-metric-chip ss-metric-chip--medium">
                        <span className="ss-metric-icon">
                          <i className="fa fa-language" aria-hidden="true" />
                        </span>
                        <div>
                          <div className="ss-metric-label">Medium</div>
                          <div
                            className="ss-metric-value ss-metric-value--text"
                            title={session.medium || "—"}
                          >
                            {session.medium || "—"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
        )}
      </div>

      <style>{`
        .ss-page {
          padding-bottom: 24px;
          background: #ffffff !important;
        }
        .ss-hero {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          flex-wrap: wrap;
          margin-bottom: 15px;
          padding: 13px 16px;
          border-radius: 13px;
          background: linear-gradient(135deg, var(--ss-accent) 0%, ${COLORS.primary_color_shade7 || "#0288D1"} 100%);
          color: #fff;
          box-shadow: 0 4px 16px color-mix(in srgb, var(--ss-accent) 35%, transparent);
        }
        .ss-hero-main {
          display: flex;
          align-items: center;
          gap: 11px;
          min-width: 0;
          flex: 1;
        }
        .ss-hero-icon {
          width: 40px;
          height: 40px;
          border-radius: 11px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 17px;
          flex-shrink: 0;
          color: #fff;
          background: rgba(255, 255, 255, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.28);
        }
        .ss-heading {
          margin: 0;
          font-size: 18px;
          font-weight: 800;
          line-height: 1.2;
          color: #fff;
        }
        .ss-subheading {
          margin: 2px 0 0;
          font-size: 12px;
          line-height: 1.25;
          color: rgba(255, 255, 255, 0.88);
        }
        .ss-hero-total {
          text-align: center;
          padding: 6px 14px;
          border-radius: 11px;
          background: rgba(255, 255, 255, 0.14);
          border: 1px solid rgba(255, 255, 255, 0.24);
          min-width: 92px;
        }
        .ss-hero-total-label {
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          opacity: 0.9;
          line-height: 1.2;
        }
        .ss-hero-total-value {
          font-size: 24px;
          font-weight: 800;
          line-height: 1.12;
          margin-top: 2px;
        }
        .ss-add-btn {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          padding: 10px 18px;
          border-radius: 999px;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 0.01em;
          color: var(--ss-accent);
          text-decoration: none;
          background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
          border: 1px solid rgba(255, 255, 255, 0.55);
          box-shadow: 0 4px 14px rgba(15, 23, 42, 0.14), inset 0 1px 0 rgba(255, 255, 255, 0.9);
          transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
          flex-shrink: 0;
          cursor: pointer;
        }
        .ss-add-btn-icon {
          width: 28px;
          height: 28px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          color: #fff;
          background: linear-gradient(135deg, var(--ss-accent) 0%, ${COLORS.primary_color_shade7 || "#0288D1"} 100%);
          box-shadow: 0 2px 8px color-mix(in srgb, var(--ss-accent) 35%, transparent);
        }
        .ss-add-btn:hover {
          color: var(--ss-accent);
          text-decoration: none;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.95);
        }
        .ss-add-btn:active {
          transform: translateY(0);
        }
        .ss-loader-wrap {
          display: flex;
          justify-content: center;
          padding: 48px 0;
        }
        .ss-empty {
          text-align: center;
          padding: 48px 20px;
          color: #6b7280;
        }
        .ss-empty-icon {
          width: 56px;
          height: 56px;
          margin: 0 auto 14px;
          border-radius: 16px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          color: #9ca3af;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
        }
        .ss-empty-title {
          font-size: 16px;
          font-weight: 700;
          color: #374151;
          margin-bottom: 6px;
        }
        .ss-empty-text {
          font-size: 13px;
        }
        .ss-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .ss-card {
          position: relative;
          border-radius: 14px;
          background: #ffffff;
          border: 2px solid ${COLORS.grey3 || "#E0E0E0"};
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          overflow: hidden;
          transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
        }
        .ss-card--current {
          border-color: #86efac;
        }
        .ss-card--current:hover {
          border-color: #22c55e;
          box-shadow: 0 6px 18px rgba(34, 197, 94, 0.15);
        }
        .ss-card--upcoming {
          border-color: #93c5fd;
        }
        .ss-card--upcoming:hover {
          border-color: #3b82f6;
          box-shadow: 0 6px 18px rgba(59, 130, 246, 0.15);
        }
        .ss-card--previous {
          border-color: #fdba74;
        }
        .ss-card--previous:hover {
          border-color: #f97316;
          box-shadow: 0 6px 18px rgba(249, 115, 22, 0.15);
        }
        .ss-card--active {
          border-color: #5eead4;
        }
        .ss-card--active:hover {
          border-color: #14b8a6;
          box-shadow: 0 6px 18px rgba(20, 184, 166, 0.15);
        }
        .ss-card:hover {
          transform: translateY(-1px);
        }
        .ss-card:not(.ss-card--current):not(.ss-card--upcoming):not(.ss-card--previous):not(.ss-card--active):hover {
          border-color: color-mix(in srgb, var(--ss-accent) 35%, #e5e7eb);
          box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
        }
        .ss-card-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 12px 16px 10px;
          border-bottom: 1px solid #f1f5f9;
        }
        .ss-card-title-wrap {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
          flex: 1;
        }
        .ss-card-title-block {
          flex: 1;
          min-width: 0;
        }
        .ss-card-aside {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex-shrink: 0;
          min-width: 0;
        }
        .ss-action-group {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex-wrap: nowrap;
          gap: 8px;
        }
        .ss-action-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          min-width: 112px;
          padding: 6px 12px 6px 7px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.01em;
          cursor: pointer;
          border: 1px solid transparent;
          box-shadow: 0 2px 6px rgba(15, 23, 42, 0.07);
          transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease, border-color 0.15s ease;
          flex-shrink: 0;
          white-space: nowrap;
        }
        .ss-action-btn-icon {
          width: 22px;
          height: 22px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          flex-shrink: 0;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.35);
        }
        .ss-action-btn-text {
          line-height: 1.1;
          white-space: nowrap;
        }
        .ss-action-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 3px 10px rgba(15, 23, 42, 0.1);
        }
        .ss-action-btn:active:not(:disabled) {
          transform: translateY(0);
        }
        .ss-action-btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }
        .ss-action-btn--cycle {
          color: #0369a1;
          background: linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 100%);
          border-color: #bae6fd;
        }
        .ss-action-btn--cycle .ss-action-btn-icon {
          color: #fff;
          background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
        }
        .ss-action-btn--cycle:hover:not(:disabled) {
          background: linear-gradient(180deg, #e0f2fe 0%, #bae6fd 100%);
          border-color: #7dd3fc;
        }
        .ss-action-btn--name {
          color: #6d28d9;
          background: linear-gradient(180deg, #f5f3ff 0%, #ede9fe 100%);
          border-color: #ddd6fe;
        }
        .ss-action-btn--name .ss-action-btn-icon {
          color: #fff;
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
        }
        .ss-action-btn--name:hover:not(:disabled) {
          background: linear-gradient(180deg, #ede9fe 0%, #ddd6fe 100%);
          border-color: #c4b5fd;
        }
        .ss-action-btn--remove {
          color: #b91c1c;
          background: linear-gradient(180deg, #fef2f2 0%, #fee2e2 100%);
          border-color: #fecaca;
        }
        .ss-action-btn--remove .ss-action-btn-icon {
          color: #fff;
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
        }
        .ss-action-btn--remove:hover:not(:disabled) {
          background: linear-gradient(180deg, #fee2e2 0%, #fecaca 100%);
          border-color: #fca5a5;
        }
        .ss-session-icon {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          color: var(--ss-accent);
          background: color-mix(in srgb, var(--ss-accent) 10%, #ffffff);
          border: 1px solid color-mix(in srgb, var(--ss-accent) 20%, #e5e7eb);
        }
        .ss-session-name {
          font-size: 17px;
          font-weight: 800;
          color: ${COLORS.black8 || "#1e293b"};
          line-height: 1.2;
        }
        .ss-date-row {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 5px;
          font-size: 12px;
        }
        .ss-date-row .fa {
          font-size: 11px;
          color: var(--ss-accent);
        }
        .ss-date-range {
          display: inline-flex;
          align-items: center;
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.01em;
          color: var(--ss-accent);
          background: color-mix(in srgb, var(--ss-accent) 12%, #ffffff);
          border: 1px solid color-mix(in srgb, var(--ss-accent) 28%, #e5e7eb);
          box-shadow: 0 1px 4px color-mix(in srgb, var(--ss-accent) 12%, transparent);
        }
        .ss-status {
          flex-shrink: 0;
          font-size: 10px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 5px 11px;
          border-radius: 999px;
          border: 2px solid transparent;
          box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08);
          margin-left: 2px;
        }
        .ss-status--current {
          color: #ffffff;
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
          border-color: #15803d;
          box-shadow: 0 2px 10px rgba(34, 197, 94, 0.35);
        }
        .ss-status--upcoming {
          color: #ffffff;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          border-color: #1d4ed8;
          box-shadow: 0 2px 10px rgba(59, 130, 246, 0.35);
        }
        .ss-status--previous {
          color: #ffffff;
          background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
          border-color: #c2410c;
          box-shadow: 0 2px 10px rgba(249, 115, 22, 0.35);
        }
        .ss-status--active {
          color: #ffffff;
          background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
          border-color: #0f766e;
          box-shadow: 0 2px 10px rgba(20, 184, 166, 0.35);
        }
        .ss-status--inactive,
        .ss-status--closed {
          color: #64748b;
          background: #f8fafc;
          border-color: #e2e8f0;
        }
        .ss-metrics {
          display: grid;
          grid-template-columns: repeat(6, minmax(0, 1fr));
          gap: 10px;
          padding: 12px 16px 14px;
        }
        .ss-metric-chip {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          min-width: 0;
          padding: 10px;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          background: #ffffff;
        }
        .ss-metric-icon {
          width: 30px;
          height: 30px;
          border-radius: 9px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          flex-shrink: 0;
        }
        .ss-metric-label {
          font-size: 9px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #94a3b8;
          line-height: 1.2;
        }
        .ss-metric-value {
          font-size: 15px;
          font-weight: 800;
          color: #1e293b;
          line-height: 1.2;
          margin-top: 2px;
        }
        .ss-metric-subvalue {
          font-size: 10px;
          font-weight: 700;
          color: #94a3b8;
          line-height: 1.2;
          margin-top: 2px;
        }
        .ss-metric-value--text {
          font-size: 12px;
          font-weight: 700;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 140px;
        }
        .ss-metric-chip--students .ss-metric-icon {
          background: color-mix(in srgb, #3b82f6 14%, #ffffff);
          color: #2563eb;
        }
        .ss-metric-chip--classes .ss-metric-icon {
          background: color-mix(in srgb, #8b5cf6 14%, #ffffff);
          color: #7c3aed;
        }
        .ss-metric-chip--fee .ss-metric-icon {
          background: color-mix(in srgb, #f59e0b 14%, #ffffff);
          color: #d97706;
        }
        .ss-metric-chip--fee-done .ss-metric-icon {
          background: color-mix(in srgb, #22c55e 14%, #ffffff);
          color: #16a34a;
        }
        .ss-metric-chip--fee-done .ss-metric-value {
          color: #16a34a;
        }
        .ss-metric-chip--subject .ss-metric-icon {
          background: color-mix(in srgb, #6366f1 14%, #ffffff);
          color: #4f46e5;
        }
        .ss-metric-chip--subject-done .ss-metric-icon {
          background: color-mix(in srgb, #22c55e 14%, #ffffff);
          color: #16a34a;
        }
        .ss-metric-chip--subject-done .ss-metric-value {
          color: #16a34a;
        }
        .ss-metric-chip--board .ss-metric-icon {
          background: color-mix(in srgb, var(--ss-accent) 14%, #ffffff);
          color: var(--ss-accent);
        }
        .ss-metric-chip--medium .ss-metric-icon {
          background: color-mix(in srgb, #14b8a6 14%, #ffffff);
          color: #0d9488;
        }
        @media (max-width: 1100px) {
          .ss-metrics {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }
        @media (max-width: 900px) {
          .ss-metrics {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        @media (max-width: 768px) {
          .ss-hero {
            flex-direction: column;
            align-items: stretch;
          }
          .ss-hero-total {
            width: 100%;
          }
          .ss-add-btn {
            justify-content: center;
            width: 100%;
          }
          .ss-card-head {
            flex-direction: column;
            align-items: stretch;
          }
          .ss-card-aside {
            width: 100%;
            justify-content: flex-start;
          }
          .ss-action-group {
            flex-wrap: wrap;
            justify-content: flex-start;
          }
          .ss-action-btn {
            min-width: 0;
          }
          .ss-status {
            margin-left: 0;
          }
          .ss-metric-value--text {
            max-width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

const SessionSettings = () => {
  const [loggedIn] = useState(!!localStorage.getItem("token"));

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  return <SessionSettingsList />;
};

export default SessionSettings;
